import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Briefcase, ChevronLeft, MapPin, SearchX } from "@/lib/public-icons";
import { PortalNotice } from "@/components/app/patterns/portal-notice";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useSession } from "@/lib/host/auth-client";
import { ApplicationMessages } from "./-components/application-messages";
import { PortalShell } from "./-components/portal-shell";
import { StageFocus } from "./-components/stage-focus";

export const Route = createFileRoute("/applicant-portal/$applicationId")({
  component: ApplicationPage,
});

/** One application: where it is, what was asked for, and the conversation. */
function ApplicationPage() {
  const { applicationId } = Route.useParams();
  const session = useSession();

  const enabled = Boolean(session.data);
  const application = useQuery({
    ...orpc.applicantPortal.application.queryOptions({ input: { applicationId } }),
    retry: false,
    enabled,
  });
  // Asked for here, with the application, so the conversation is not left to
  // start loading only once the page around it has.
  useQuery({
    ...orpc.applicantPortal.messages.queryOptions({ input: { applicationId } }),
    retry: false,
    enabled,
  });

  if (!session.data) {
    return (
      <PortalShell>
        <PortalNotice
          icon={<Briefcase />}
          title="Sign in to see this application"
          action={
            <Button asChild size="lg">
              <Link to="/login" search={{ next: `/applicant-portal/${applicationId}` }}>
                Sign in
              </Link>
            </Button>
          }
        >
          Use the email address you applied with.
        </PortalNotice>
      </PortalShell>
    );
  }

  if (application.isError) {
    return (
      <PortalShell name={session.data.user.name}>
        <PortalNotice
          icon={<SearchX />}
          tone="idle"
          title="This application is not here"
          action={
            <Button asChild variant="outline">
              <Link to="/applicant-portal">See your applications</Link>
            </Button>
          }
        >
          It may have been withdrawn. Your other applications are on the main page.
        </PortalNotice>
      </PortalShell>
    );
  }

  return (
    <PortalShell name={session.data.user.name} brand={application.data?.facility ?? null}>
      <Link
        className="text-muted-foreground hover:text-foreground mb-6 flex items-center gap-1 text-sm"
        to="/applicant-portal"
      >
        <ChevronLeft className="size-4" />
        All your applications
      </Link>

      {application.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <div className="space-y-6">
          <div>
            <h1 className="font-display text-2xl text-balance">{application.data?.job?.title}</h1>
            <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              {application.data?.facility?.name ? (
                <span>{application.data.facility.name}</span>
              ) : null}
              {application.data?.job?.location ? (
                <span className="flex items-center gap-1">
                  <MapPin className="size-3" />
                  {application.data.job.location}
                </span>
              ) : null}
            </div>
            <Badge className="mt-3" variant="neutral">
              {application.data?.stageLabel}
            </Badge>
          </div>

          <StageFocus
            applicationId={applicationId}
            currentStage={application.data?.currentStage ?? ""}
          />

          <ApplicationMessages applicationId={applicationId} />
        </div>
      )}
    </PortalShell>
  );
}
